import { Tournament, Player, Round, MatchPlay } from '../types';
import { syncTournament, syncPlayers, syncRounds, syncMatches, supabase } from './supabase';
import { courses as defaultCourses } from '../data/courses';

const SYNC_RETRY_ATTEMPTS = 3;
const SYNC_RETRY_DELAY = 1000;

const STORAGE_KEYS = {
  TOURNAMENTS: 'hvga_tournaments',
  PLAYERS: 'hvga_players',
  MATCHES: 'hvga_matches',
  PENDING_SYNC: 'hvga_pending_sync'
};

// Add timestamp to stored data
interface TimestampedData<T> {
  data: T;
  timestamp: number;
  version: number;
  pendingSync?: boolean;
}

const CURRENT_DATA_VERSION = 1;

function saveToLocalStorage<T>(key: string, data: T): void {
  try {
    const timestampedData: TimestampedData<T> = {
      data,
      timestamp: Date.now(),
      version: CURRENT_DATA_VERSION,
      pendingSync: !navigator.onLine
    };
    localStorage.setItem(key, JSON.stringify(timestampedData));
    
    // Track pending syncs
    if (!navigator.onLine) {
      const pendingSyncs = loadFromLocalStorage<string[]>(STORAGE_KEYS.PENDING_SYNC) || [];
      if (!pendingSyncs.includes(key)) {
        saveToLocalStorage(STORAGE_KEYS.PENDING_SYNC, [...pendingSyncs, key]);
      }
    }
  } catch (error) {
    console.error(`Error saving to localStorage (${key}):`, error);
  }
}

function loadFromLocalStorage<T>(key: string): T | null {
  try {
    const data = localStorage.getItem(key);
    if (!data) return null;
    const timestampedData: TimestampedData<T> = JSON.parse(data);
    
    // Handle version upgrades if needed
    if (!timestampedData.version || timestampedData.version < CURRENT_DATA_VERSION) {
      console.log(`Upgrading data format for ${key}`);
      // Add any necessary data migration logic here
    }
    
    return timestampedData.data;
  } catch (error) {
    console.error(`Error loading from localStorage (${key}):`, error);
    return null;
  }
}

async function retrySync<T>(
  syncFn: () => Promise<void>,
  attempts: number = SYNC_RETRY_ATTEMPTS
): Promise<void> {
  for (let i = 0; i < attempts; i++) {
    try {
      await syncFn();
      return;
    } catch (error) {
      if (i === attempts - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, SYNC_RETRY_DELAY * Math.pow(2, i)));
    }
  }
}

function clearLocalStorage(): void {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
}

// Tournaments operations
async function saveTournaments(tournaments: Tournament[]): Promise<void> {
  if (!Array.isArray(tournaments)) {
    console.error('Invalid tournaments data:', tournaments);
    saveToLocalStorage(STORAGE_KEYS.TOURNAMENTS, []);
    return;
  }

  console.log('Saving tournaments:', tournaments);

  // Validate tournament status
  const validStatuses = ['draft', 'registration', 'active', 'completed'];

  // Add timestamp to stored tournaments
  const timestampedTournaments = tournaments.map(t => ({
    ...t,
    lastModified: Date.now(),
    // Ensure status is valid
    status: validStatuses.includes(t.status || '') ? t.status : 'draft'
  }));

  // Validate tournaments before saving
  const validTournaments = timestampedTournaments.filter(t => {
    if (!t?.startDate || !t?.endDate) {
      console.warn('Invalid tournament data - missing dates:', t);
      return false;
    }
    if (!t?.id) {
      console.warn('Invalid tournament data - missing id:', t);
      return false;
    }
    if (!validStatuses.includes(t.status)) {
      console.warn('Invalid tournament status:', t.status);
      return false;
    }
    return true;
  });

  if (validTournaments.length === 0) {
    console.warn('No valid tournaments to save');
    saveToLocalStorage(STORAGE_KEYS.TOURNAMENTS, []);
    return;
  }

  saveToLocalStorage(STORAGE_KEYS.TOURNAMENTS, validTournaments);

  // Handle offline/unconfigured states silently
  if (!supabase) {
    return;
  }
  
  if (!navigator.onLine) {
    // Track for later sync
    const pendingSyncs = loadFromLocalStorage<string[]>(STORAGE_KEYS.PENDING_SYNC) || [];
    if (!pendingSyncs.includes(STORAGE_KEYS.TOURNAMENTS)) {
      saveToLocalStorage(STORAGE_KEYS.PENDING_SYNC, [...pendingSyncs, STORAGE_KEYS.TOURNAMENTS]);
    }
    return;
  }
  
  // Sync each tournament individually to handle errors better
  try {
    for (const tournament of validTournaments) {
      try {
        const { lastModified, ...tournamentData } = tournament;
        console.log('Syncing tournament to Supabase:', {
          id: tournamentData.id,
          name: tournamentData.name,
          status: tournamentData.status
        });

        const { error } = await supabase
          .from('tournaments')
          .upsert({
            id: tournamentData.id,
            name: tournamentData.name,
            start_date: tournamentData.startDate || null,
            end_date: tournamentData.endDate || null,
            course_id: tournamentData.courseId,
            selected_tee: tournamentData.selectedTee,
            format: tournamentData.format,
            status: tournamentData.status,
            scoring_type: tournamentData.scoringType,
            metadata: tournamentData.metadata || {},
            player_limit: tournamentData.playerLimit,
            registration_deadline: tournamentData.registrationDeadline || null,
            rules: tournamentData.rules || ''
          });

        if (error) {
          console.error('Supabase upsert error:', error);
          throw error;
        }
        console.log('Tournament synced successfully');
      } catch (error) {
        // Only log actual errors, not connection issues
        if (error instanceof Error && !error.message.includes('Failed to fetch')) {
          console.error(`Failed to sync tournament ${tournament.id}:`, error);
        }
        // Track failed sync for retry
        const pendingSyncs = loadFromLocalStorage<string[]>(STORAGE_KEYS.PENDING_SYNC) || [];
        if (!pendingSyncs.includes(STORAGE_KEYS.TOURNAMENTS)) {
          saveToLocalStorage(STORAGE_KEYS.PENDING_SYNC, [...pendingSyncs, STORAGE_KEYS.TOURNAMENTS]);
        }
      }
    }
  } catch (error) {
    // Only log actual errors, not connection issues
    if (error instanceof Error && !error.message.includes('Failed to fetch')) {
      console.error('Failed to sync tournaments with Supabase:', error);
    }
    // Track failed sync for retry
    const pendingSyncs = loadFromLocalStorage<string[]>(STORAGE_KEYS.PENDING_SYNC) || [];
    if (!pendingSyncs.includes(STORAGE_KEYS.TOURNAMENTS)) {
      saveToLocalStorage(STORAGE_KEYS.PENDING_SYNC, [...pendingSyncs, STORAGE_KEYS.TOURNAMENTS]);
    }
  }
}

function loadTournaments(): Tournament[] {
  const tournaments = loadFromLocalStorage<Tournament[]>(STORAGE_KEYS.TOURNAMENTS);
  return Array.isArray(tournaments) 
    ? tournaments.map(({ lastModified, ...t }) => t) 
    : [];
}

// Player operations
async function savePlayers(players: Player[]): Promise<void> {
  saveToLocalStorage(STORAGE_KEYS.PLAYERS, players);
  if (!supabase || !navigator.onLine) return;
  
  try {
    await retrySync(() => syncPlayers(players));
  } catch (error) {
    // Only log actual errors, not connection issues
    if (error instanceof Error && 
        !error.message.includes('Failed to fetch') && 
        !error.message.includes('not configured')) {
      console.error('Failed to sync players with Supabase:', error);
    }
  }
}

function loadPlayers(): Player[] {
  return loadFromLocalStorage(STORAGE_KEYS.PLAYERS) || [];
}

// Match operations
async function saveMatches(matches: MatchPlay[]): Promise<void> {
  saveToLocalStorage(STORAGE_KEYS.MATCHES, matches);
  if (!supabase) return;
  
  try {
    await retrySync(() => syncMatches(matches));
  } catch (error) {
    console.error('Failed to sync matches with Supabase:', error);
  }
}

function loadMatches(): MatchPlay[] {
  return loadFromLocalStorage(STORAGE_KEYS.MATCHES) || [];
}

// Load all data at once
function loadAllLocalData() {
  return {
    tournaments: loadTournaments() || [],
    players: loadPlayers() || [],
    matches: loadMatches() || []
  };
}

// Reset all data
async function resetAllData() {
  try {
    // If Supabase is available, clear remote data
    if (supabase) {
      try {
        // Delete in correct order to handle foreign key constraints
        await Promise.all([
          // Delete matches with WHERE clause
          supabase.from('matches').delete().neq('id', '00000000-0000-0000-0000-000000000000'),
          // Delete players with WHERE clause
          supabase.from('players').delete().neq('id', '00000000-0000-0000-0000-000000000000'),
          // Delete tournaments with WHERE clause
          supabase.from('tournaments').delete().neq('id', '00000000-0000-0000-0000-000000000000')
        ]);
      } catch (error) {
        console.error('Failed to reset Supabase data:', error);
        throw error;
      }
    }

    // Clear local storage after successful remote clear
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });

    // Initialize empty state
    saveToLocalStorage(STORAGE_KEYS.PLAYERS, []);
    saveToLocalStorage(STORAGE_KEYS.MATCHES, []);
    saveToLocalStorage(STORAGE_KEYS.PENDING_SYNC, []);
    saveToLocalStorage(STORAGE_KEYS.TOURNAMENTS, []);

    return null;
  } catch (error) {
    console.error('Failed to reset data:', error);
    throw error;
  }
}


export {
  saveToLocalStorage,
  loadFromLocalStorage,
  clearLocalStorage,
  saveTournaments,
  loadTournaments,
  savePlayers,
  loadPlayers,
  saveMatches,
  loadMatches,
  loadAllLocalData,
  resetAllData
};